import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Server } from 'lucide-react';
import { api } from '../../utils/api';
import {
  TraceHopItem,
  formatHopRackLabel,
  canJumpToFarEnd,
  buildFarEndNavigationUrl,
} from '../../utils/cableTraceFarEnd.logic';

export type TraceHop = TraceHopItem;

interface VisualCableTraceProps {
  dcCode: string;
  workspace: string;
  startPortId: string;
  currentRackId?: number | string | null;
  currentRackName?: string | null;
  onClose: () => void;
  onNavigateToFarEnd?: (url: string) => void;
}

export const VisualCableTrace: React.FC<VisualCableTraceProps> = ({
  dcCode,
  workspace,
  startPortId,
  currentRackId,
  currentRackName,
  onClose,
  onNavigateToFarEnd,
}) => {
  const navigate = useNavigate();
  const [trace, setTrace] = useState<TraceHop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchTrace = async () => {
      setLoading(true);
      setError(null);
      setTrace([]);
      try {
        const res = await api.get(`/api/datacenters/${dcCode}/connectivity/trace/${startPortId}`, { params: { workspace } });
        if (active) setTrace(res.data.trace || []);
      } catch (err: any) {
        if (active) setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Не удалось загрузить трассировку.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchTrace();
    return () => { active = false; };
  }, [dcCode, startPortId, workspace]);

  const canJump = canJumpToFarEnd(trace, currentRackId);

  const handleJump = (farEndHop: TraceHop) => {
    const url = buildFarEndNavigationUrl(dcCode, farEndHop, currentRackId, currentRackName);
    if (url) {
      if (onNavigateToFarEnd) {
        onNavigateToFarEnd(url);
      } else {
        navigate(url);
      }
      onClose();
    }
  };

  if (loading) {
    return <div className="p-4 text-cyan-400 font-mono animate-pulse">Tracing signal path...</div>;
  }

  if (error || trace.length === 0) {
    return (
      <div role="alert" className="p-4 border border-red-500/50 bg-red-950/20 rounded text-red-400 font-mono text-sm">
        <div className="flex justify-between items-center mb-2">
          <span className="font-bold">Трассировка недоступна</span>
          <button onClick={onClose} aria-label="Закрыть трассировку" className="hover:text-red-200 cursor-pointer">✕</button>
        </div>
        {error || 'Маршрут от этого порта не найден.'}
      </div>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-2xl p-4 flex flex-col max-h-[80vh] w-[420px]">
      <div className="flex justify-between items-center mb-4 border-b border-slate-800 pb-2">
        <h3 className="text-cyan-400 font-mono font-bold flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          End-to-End Visual Trace
        </h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors cursor-pointer">✕</button>
      </div>

      <div className="overflow-y-auto flex-1 pr-2 space-y-2 font-mono text-sm">
        {trace.map((hop, idx) => (
          <div key={idx} className="relative">
            {/* Hop Node */}
            <div className="bg-slate-800 border border-slate-700 rounded p-3 relative z-10 flex flex-col gap-1.5 shadow-sm">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div
                    className="text-slate-200 font-bold truncate text-sm"
                    title={formatHopRackLabel(hop.equipment_name, hop.rack_name)}
                  >
                    {hop.equipment_name}
                  </div>
                  {hop.rack_name && (
                    <div className="text-[11px] text-slate-400 font-mono flex items-center gap-1 mt-0.5">
                      <Server className="w-3 h-3 text-cyan-400/80 shrink-0" />
                      <span className="truncate">Стойка {hop.rack_name}</span>
                    </div>
                  )}
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded bg-slate-900 text-slate-400 border border-slate-700 uppercase font-semibold shrink-0">
                  {hop.equipment_type}
                </span>
              </div>

              <div className="flex items-center justify-between gap-2 mt-1">
                <div className="flex items-center gap-2 text-cyan-300 font-semibold text-xs truncate">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee] shrink-0"></div>
                  <span className="truncate">Порт: {hop.port_name}</span>
                </div>
                {idx === 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-950/80 text-cyan-300 border border-cyan-700/60 font-mono shrink-0">
                    Начало
                  </span>
                )}
                {idx === trace.length - 1 && trace.length > 1 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-950/80 text-emerald-300 border border-emerald-700/60 font-mono shrink-0">
                    Удаленный конец
                  </span>
                )}
              </div>

              {/* Far-End Jump button on the final hop */}
              {idx === trace.length - 1 && canJump && (
                <div className="mt-2.5 pt-2.5 border-t border-slate-700/80 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-400">Удаленная точка</span>
                  <button
                    type="button"
                    onClick={() => handleJump(hop)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-700 text-white shadow-md shadow-cyan-950/50 transition-colors cursor-pointer"
                    title={`Перейти к стойке ${hop.rack_name || hop.rack_id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Перейти к удаленному концу</span>
                  </button>
                </div>
              )}
            </div>

            {/* Connection Link */}
            {hop.connection && (
              <div className="flex flex-col items-center justify-center my-1 relative">
                <div className="w-0.5 h-6 bg-slate-600"></div>
                {hop.connection.type === 'cable' ? (
                  <div 
                    className="absolute z-20 px-2 py-1 rounded text-[10px] font-bold shadow-lg border"
                    style={{
                      backgroundColor: hop.connection.color || '#3b82f6',
                      borderColor: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                    }}
                  >
                    {hop.connection.cable_type || 'Кабель'} (Cable)
                  </div>
                ) : (
                  <div className="absolute z-20 px-2 py-1 rounded text-[10px] font-bold bg-slate-700 border border-slate-500 text-slate-300">
                    Internal Pass-through
                  </div>
                )}
                <div className="w-0.5 h-6 bg-slate-600"></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
